/** What the site finder filters and sorts a body's sites on, derived once per body from the registry rows. */

import type { Site, SiteKind } from './siteRegistry.ts';

export const SITE_KINDS: readonly { id: SiteKind; label: string }[] = [
  { id: 'landing', label: 'LANDING SITE' },
  { id: 'feature', label: 'NAMED FEATURE' },
  { id: 'canon', label: 'CANON PLACE' },
];

/** Instrument families, matched on the row's own instrument string; each label
 *  names the family's finest product rather than any one row's. */
const SOURCE_FAMILIES: readonly { id: string; re: RegExp; label: string }[] = [
  { id: 'hirise', re: /hirise/i, label: 'HIRISE 1 M' },
  { id: 'nac', re: /\bnac\b/i, label: 'LROC NAC 2 M' },
  { id: 'ctx', re: /\bctx\b/i, label: 'CTX 20 M' },
  { id: 'hrsc', re: /\bhrsc\b/i, label: 'HRSC 50 M' },
];

export type SiteSort = 'name' | 'distance' | 'size';

export interface LatLonBox {
  latMin: number;
  latMax: number;
  /** East longitude 0-360; the box runs east from `lonMin` for `lonSpan` degrees. */
  lonMin: number;
  lonSpan: number;
}

export interface SiteFacetRow {
  /** Name, aliases, product id, landing mission and quad, folded. */
  text: string;
  /** Name and quad only: the filter this finder replaced, kept as its control. */
  nameQuad: string;
  kind: SiteKind;
  source: string;
  ns: 'N' | 'S';
  ew: 'E' | 'W';
  chart: string;
  areaKm2: number;
  box: LatLonBox;
}

export interface SiteFacets {
  rows: SiteFacetRow[];
  /** Registry indices in name order, ties in registry order. */
  byName: number[];
  /** Options present on this body, in the order the selects list them. */
  charts: { id: string; label: string; count: number }[];
  sources: { id: string; label: string; count: number }[];
  kinds: { id: SiteKind; label: string; count: number }[];
}

export interface SiteQuery {
  /** Already folded by the caller's own normaliser. */
  text: string;
  ns: '' | 'N' | 'S';
  ew: '' | 'E' | 'W';
  chart: string;
  source: string;
  kind: '' | SiteKind;
  /** Sites whose footprint meets this box; null is no globe pick. */
  pick: LatLonBox | null;
}

export const EMPTY_SITE_QUERY: SiteQuery = { text: '', ns: '', ew: '', chart: '', source: '', kind: '', pick: null };

const wrap360 = (lon: number): number => ((lon % 360) + 360) % 360;

export function siteKind(st: Site): SiteKind {
  if (st.kind === 'landing' || st.kind === 'feature' || st.kind === 'canon') return st.kind;
  return st.landing_mission ? 'landing' : 'feature';
}

export function siteSource(st: Site): { id: string; label: string } {
  const fam = SOURCE_FAMILIES.find((f) => f.re.test(st.instrument));
  if (fam) return { id: fam.id, label: fam.label };
  const label = st.instrument.trim().toUpperCase() || 'UNNAMED SOURCE';
  return { id: `other:${label}`, label };
}

/** The row's own baked window, else a square the size of its survey round its centre. */
export function siteBox(st: Site, bodyRadiusKm: number): LatLonBox {
  const fp = st.footprint;
  if (fp) {
    const lonMin = wrap360(fp.lon_min_deg);
    const span = wrap360(fp.lon_max_deg - fp.lon_min_deg);
    return { latMin: fp.lat_min_deg, latMax: fp.lat_max_deg, lonMin, lonSpan: span === 0 ? 360 : span };
  }
  const halfKm = Math.sqrt(Math.max(0, st.plots * st.plot_area_km2)) / 2;
  return boxAround(st.center_lat_deg, st.center_lon_deg, halfKm, bodyRadiusKm);
}

/** A lat/lon box `halfKm` either side of a point, widened in longitude by latitude. */
export function boxAround(lat: number, lon: number, halfKm: number, bodyRadiusKm: number): LatLonBox {
  const dLat = (halfKm / Math.max(1, bodyRadiusKm)) * (180 / Math.PI);
  const cos = Math.cos((lat * Math.PI) / 180);
  const dLon = cos < 1e-3 ? 180 : Math.min(180, dLat / cos);
  return {
    latMin: Math.max(-90, lat - dLat),
    latMax: Math.min(90, lat + dLat),
    lonMin: wrap360(lon - dLon),
    lonSpan: dLon >= 180 || lat + dLat >= 90 || lat - dLat <= -90 ? 360 : 2 * dLon,
  };
}

export function boxesMeet(a: LatLonBox, b: LatLonBox): boolean {
  if (a.latMax < b.latMin || b.latMax < a.latMin) return false;
  if (a.lonSpan >= 360 || b.lonSpan >= 360) return true;
  const d = wrap360(b.lonMin - a.lonMin);
  return d <= a.lonSpan || d >= 360 - b.lonSpan;
}

function boxAreaKm2(b: LatLonBox, bodyRadiusKm: number): number {
  const d = Math.PI / 180;
  return bodyRadiusKm * bodyRadiusKm * (b.lonSpan * d)
    * Math.abs(Math.sin(b.latMax * d) - Math.sin(b.latMin * d));
}

/** Separation between two body points, degrees. */
export function arcDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const d = Math.PI / 180;
  const c = Math.sin(lat1 * d) * Math.sin(lat2 * d)
    + Math.cos(lat1 * d) * Math.cos(lat2 * d) * Math.cos((lon2 - lon1) * d);
  return (Math.acos(Math.min(1, Math.max(-1, c))) * 180) / Math.PI;
}

export function buildSiteFacets(
  rows: readonly Site[],
  opts: {
    norm: (s: string) => string;
    chartOf: (st: Site) => { id: string; label: string };
    bodyRadiusKm: number;
  },
): SiteFacets {
  const out: SiteFacetRow[] = [];
  const charts = new Map<string, { id: string; label: string; count: number }>();
  const sources = new Map<string, { id: string; label: string; count: number }>();
  const kindCount = new Map<SiteKind, number>();
  for (const st of rows) {
    const chart = opts.chartOf(st);
    const source = siteSource(st);
    const kind = siteKind(st);
    const box = siteBox(st, opts.bodyRadiusKm);
    const parts = [st.name, ...(st.aliases ?? []), st.product_id ?? '', st.landing_mission ?? '', st.quad];
    out.push({
      text: opts.norm(parts.join(' | ')),
      nameQuad: `${opts.norm(st.name)} | ${opts.norm(st.quad)}`,
      kind,
      source: source.id,
      ns: st.center_lat_deg >= 0 ? 'N' : 'S',
      ew: wrap360(st.center_lon_deg) < 180 ? 'E' : 'W',
      chart: chart.id,
      areaKm2: boxAreaKm2(box, opts.bodyRadiusKm),
      box,
    });
    const c = charts.get(chart.id);
    if (c) c.count++; else charts.set(chart.id, { ...chart, count: 1 });
    const s = sources.get(source.id);
    if (s) s.count++; else sources.set(source.id, { ...source, count: 1 });
    kindCount.set(kind, (kindCount.get(kind) ?? 0) + 1);
  }
  // Numeric, so "Crater 9" files before "Crater 10".
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  const byName = rows.map((_, i) => i)
    .sort((a, b) => collator.compare(rows[a]?.name ?? '', rows[b]?.name ?? '') || a - b);
  return {
    rows: out,
    byName,
    charts: [...charts.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    sources: [...sources.values()].sort((a, b) => b.count - a.count),
    kinds: SITE_KINDS.filter((k) => kindCount.has(k.id)).map((k) => ({ ...k, count: kindCount.get(k.id) ?? 0 })),
  };
}

/**
 * The registry indices a query keeps, in name order. Every filter set narrows:
 * the groups AND together. `nameQuadOnly` is the filter this replaced, text on
 * name and quad and nothing else, kept as the finder check's control.
 */
export function matchSites(f: SiteFacets, q: SiteQuery, nameQuadOnly = false): number[] {
  const out: number[] = [];
  for (const i of f.byName) {
    const r = f.rows[i];
    if (!r) continue;
    if (nameQuadOnly) {
      if (q.text === '' || r.nameQuad.includes(q.text)) out.push(i);
      continue;
    }
    if (q.text !== '' && !r.text.includes(q.text)) continue;
    if (q.ns !== '' && r.ns !== q.ns) continue;
    if (q.ew !== '' && r.ew !== q.ew) continue;
    if (q.chart !== '' && r.chart !== q.chart) continue;
    if (q.source !== '' && r.source !== q.source) continue;
    if (q.kind !== '' && r.kind !== q.kind) continue;
    if (q.pick !== null && !boxesMeet(q.pick, r.box)) continue;
    out.push(i);
  }
  return out;
}

/** Re-order a name-ordered match list; `from` is the sub-ship point, null when there is no ship. */
export function sortSites(
  matched: number[],
  by: SiteSort,
  f: SiteFacets,
  rows: readonly Site[],
  from: { lat: number; lon: number } | null,
): number[] {
  if (by === 'size') {
    return [...matched].sort((a, b) => (f.rows[b]?.areaKm2 ?? 0) - (f.rows[a]?.areaKm2 ?? 0));
  }
  if (by === 'distance' && from !== null) {
    const deg = new Map<number, number>();
    for (const i of matched) {
      const st = rows[i];
      deg.set(i, st ? arcDeg(from.lat, from.lon, st.center_lat_deg, st.center_lon_deg) : Infinity);
    }
    return [...matched].sort((a, b) => (deg.get(a) ?? 0) - (deg.get(b) ?? 0));
  }
  return matched;
}
